import axios from 'axios';
import { toast } from 'react-toastify';
import { isAuthenticationError, handleSessionExpiration } from './authErrorHandler';
import { secureStorage } from './secureStorage'; // 🔒 NOUVEAU - Stockage sécurisé

// Configuration globale Axios
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔧 Gestion des requêtes en cours pour éviter les boucles infinies
const activeRequests = new Map();
let requestCounter = 0;

// 🛑 Fonction globale pour annuler toutes les requêtes
window.abortAllRequests = () => {
  activeRequests.forEach((abortController, requestId) => {
    try {
      abortController.abort();
    } catch (e) {
      // Ignorer les erreurs d'annulation
    }
  });
  activeRequests.clear();
  console.log('🛑 Toutes les requêtes en cours ont été annulées');
};

// Créer instance Axios avec intercepteur
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Permet l'envoi/réception de cookies httpOnly
});

// Intercepteur simplifié pour éviter les boucles
apiClient.interceptors.request.use(
  (config) => {
    // 🔧 Ajouter un AbortController pour chaque requête
    const requestId = ++requestCounter;
    const abortController = new AbortController();
    config.signal = abortController.signal;
    activeRequests.set(requestId, abortController);
    
    // Nettoyer automatiquement après la requête
    config.metadata = { requestId };
    
    // Pour les routes d'auth et admin, ne pas ajouter de tenantId
    const isAuthRoute = config.url?.includes('/auth');
    const isAdminRoute = config.url?.includes('/api/admin');
    if (isAuthRoute || isAdminRoute) {
      return config;
    }
    
    // Pour les autres routes, essayer d'ajouter tenantId si disponible
    try {
      // 🔒 SÉCURITÉ: Utiliser le stockage sécurisé en priorité
      let authData = secureStorage.getItem('authData');
      
      // Fallback: migration automatique depuis l'ancien stockage
      if (!authData) {
        const plainAuthData = localStorage.getItem('authData');
        if (plainAuthData) {
          try {
            authData = JSON.parse(plainAuthData);
            secureStorage.setItem('authData', authData); // Migrer
            localStorage.removeItem('authData'); // Nettoyer
          } catch (error) {
            console.error('❌ Erreur migration apiClient:', error);
            localStorage.removeItem('authData');
          }
        }
      }
      
      if (authData && authData.tenantId) {
        config.params = {
          ...config.params,
          tenantId: authData.tenantId
        };
      }
    } catch (error) {
      // Ignorer silencieusement les erreurs de parsing/stockage
      console.error('❌ Erreur récupération authData:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur de réponse pour gérer les erreurs d'auth
apiClient.interceptors.response.use(
  (response) => {
    // 🧹 Nettoyer la requête terminée avec succès
    const requestId = response.config.metadata?.requestId;
    if (requestId && activeRequests.has(requestId)) {
      activeRequests.delete(requestId);
    }
    return response;
  },
  (error) => {
    // 🧹 Nettoyer la requête terminée avec erreur
    const requestId = error.config?.metadata?.requestId;
    if (requestId && activeRequests.has(requestId)) {
      activeRequests.delete(requestId);
    }
    
    // Gérer les erreurs de limites API (429 - Too Many Requests)
    if (error.response?.status === 429 && error.response?.data?.code === 'API_LIMIT_EXCEEDED') {
      const { usage, upgradeOptions } = error.response.data;
      
      // Afficher un toast avec action d'achat de crédits
      const toastId = toast.error(
        <div>
          <strong>🚫 Limite d'API calls atteinte</strong>
          <p>Vous avez utilisé {usage?.used || 0} / {usage?.limit || 0} appels API ({usage?.percentage || 100}%)</p>
          <button 
            onClick={() => {
              toast.dismiss(toastId);
              window.location.href = '/marketplace';
            }}
            style={{
              marginTop: '8px',
              padding: '6px 12px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            💳 Acheter des crédits
          </button>
        </div>,
        { autoClose: 15000, closeOnClick: false }
      );
      
      return Promise.reject(error);
    }
    
    // Gérer les erreurs de limites de license (403 - Forbidden)
    if (error.response?.status === 403 && error.response?.data?.code === 'LICENSE_LIMIT_EXCEEDED') {
      const { reason, currentUsage, limits } = error.response.data;
      
      toast.error(
        <div>
          <strong>🚫 Limite de licence atteinte</strong>
          <p>{reason}</p>
          {currentUsage && limits && (
            <small>Utilisation actuelle dépasse les limites de votre plan</small>
          )}
        </div>,
        { autoClose: 10000 }
      );
      
      return Promise.reject(error);
    }
    
    // Gérer les erreurs d'authentification directement
    if (isAuthenticationError(error)) {
      const currentPath = window.location.pathname;
      if (currentPath !== '/' && !currentPath.includes('/auth') && !currentPath.includes('/login')) {
        console.log('🚨 [API Client] Session expirée détectée, redirection...');
        handleSessionExpiration();
      }
      return Promise.reject(new Error('Session expirée - redirection en cours'));
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;